import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import ScrollProgress from "./ScrollProgress";
import { NavReviewLink, MobileReviewLink } from "./GoogleReviewLink";
import { CONTACT, NAV_LINKS } from "../data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 bg-white/90 backdrop-blur-md transition-shadow ${
        scrolled ? "shadow-[0_1px_0_0_var(--line)]" : ""
      }`}
      data-testid="site-navbar"
    >
      <ScrollProgress />
      <div className="container-site xl:!max-w-7xl flex h-[72px] items-center justify-between gap-5">
        <Link to="/" className="shrink-0" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Main navigation">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `nav-link whitespace-nowrap text-[15px] font-medium transition-colors ${
                  isActive ? "active text-[var(--sage)]" : "text-[var(--ink)] hover:text-[var(--sage)]"
                }`
              }
              data-testid={`nav-link-${l.to.replace(/\//g, "")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <NavReviewLink />
          <a href={CONTACT.phoneHref} className="whitespace-nowrap text-sm font-medium text-[var(--ink)] hover:text-[var(--sage)] inline-flex items-center gap-2" data-testid="nav-phone" aria-label={`Call ${CONTACT.phone}`}>
            <Phone className="h-4 w-4" /> <span className="hidden xl:inline">{CONTACT.phone}</span>
          </a>
          <Link to="/contact" className="btn btn-primary !py-2.5 whitespace-nowrap" data-testid="nav-cta">
            Book a Session
          </Link>
        </div>

        <button
          type="button"
          className="lg:hidden p-2 -mr-2 text-[var(--ink)]"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="fade-in lg:hidden border-t border-[var(--line)] bg-white" style={{ animationDuration: "300ms" }} data-testid="nav-mobile-menu">
          <nav className="container-site py-4 flex flex-col" aria-label="Mobile navigation">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `py-3 text-base font-medium border-b border-[var(--line)] last:border-0 ${
                    isActive ? "text-[var(--sage)]" : "text-[var(--ink)]"
                  }`
                }
                data-testid={`nav-mobile-link-${l.to.replace(/\//g, "")}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="grid grid-cols-2 gap-3 pt-4">
              <a href={CONTACT.phoneHref} className="btn btn-secondary" data-testid="nav-mobile-call">
                <Phone className="h-4 w-4" /> Call
              </a>
              <Link to="/contact" className="btn btn-primary" data-testid="nav-mobile-cta">
                Book a Session
              </Link>
              <MobileReviewLink />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
